import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { Link, useNavigate } from '@tanstack/react-router';
import { Building2, Plus } from 'lucide-react';
import { useOwnerDashboardData } from './useOwnerDashboardData';
import { Select } from '@/components/ui/Select';
import { BusinessHealthRow } from '@/features/dashboard/BusinessHealthRow';
import { ActionCenterCard } from '@/features/dashboard/ActionCenterCard';
import { OccupancyOverviewCard } from '@/features/dashboard/OccupancyOverviewCard';
import { RevenueSnapshotCard } from '@/features/dashboard/RevenueSnapshotCard';
import { PropertyOverviewTable } from '@/features/dashboard/PropertyOverviewTable';
import { buildRentSearch, buildTenantSearch, pickHostelIdByBedCount } from '@/features/dashboard/drillDownTargets';
import type { DrillDownKind } from '@/features/dashboard/types';

export function OwnerDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const {
    hostels, isLoading,
    activeHostels, pending, inactive,
    multiHostel, selectedId, setSelectedId, isAll, activeHostelId,
    s, perHostelStats, revenueData,
  } = useOwnerDashboardData();
  const navigate = useNavigate();

  function handleDrillDown(kind: DrillDownKind) {
    switch (kind) {
      case 'OVERDUE_RENT':
      case 'PENDING_RENT':
      case 'EXPECTED_TODAY':
        void navigate({ to: '/owner/rent', search: buildRentSearch(kind, activeHostelId) });
        break;
      case 'CHECKIN_TODAY':
      case 'CHECKOUT_TOMORROW':
      case 'STAY_ENDING_SOON':
      case 'OCCUPIED':
      case 'RESERVED':
        void navigate({ to: '/owner/tenants', search: buildTenantSearch(kind, activeHostelId) });
        break;
      case 'MAINTENANCE':
      case 'AVAILABLE': {
        const countSelector = kind === 'MAINTENANCE'
          ? (stats: typeof s) => stats.blockedBeds
          : (stats: typeof s) => stats.vacantBeds;
        const targetHostelId = pickHostelIdByBedCount(activeHostelId, perHostelStats, countSelector);
        if (targetHostelId) {
          void navigate({
            to: '/owner/hostels/$id',
            params: { id: targetHostelId },
            search: { bedStatus: kind === 'MAINTENANCE' ? 'MAINTENANCE' : 'VACANT' },
          });
        }
        break;
      }
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.welcome', { name: user?.fullName })}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {multiHostel && (
            <Select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-auto"
              options={[
                { value: 'ALL', label: t('dashboard.allHostels') },
                ...activeHostels.map(h => ({ value: h.id, label: h.name })),
              ]}
            />
          )}
          <Link
            to="/owner/hostels"
            search={{ status: '' }}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition shrink-0"
          >
            <Plus size={16} /> {t('hostel.addHostel')}
          </Link>
        </div>
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl p-10 text-center border border-gray-100 mb-6">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('common.loading')}</p>
        </div>
      )}

      {!isLoading && (activeHostelId || isAll) && (
        <>
          {/* Section 1 — Business Health */}
          <BusinessHealthRow stats={s} onSelect={handleDrillDown} hoverBorderClassName="hover:border-teal-300" />

          {/* Section 2 — Action Center */}
          <ActionCenterCard stats={s} onSelect={handleDrillDown} />

          {/* Section 3 & 4 — Occupancy Overview + Revenue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <OccupancyOverviewCard stats={s} onSelect={handleDrillDown} />
            <RevenueSnapshotCard stats={s} revenueData={revenueData} onSelectPendingRent={handleDrillDown} accentHex="#0d9488" />
          </div>

          {/* Section 5 — Property Overview */}
          <PropertyOverviewTable entries={perHostelStats} role="owner" />
        </>
      )}

      {/* Hostel Status Row */}
      {!isLoading && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Link to="/owner/hostels" search={{ status: '' }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-teal-300 hover:shadow-md transition text-left block">
          <p className="text-2xl font-bold text-gray-900">{hostels.length}</p>
          <p className="text-sm text-gray-500">{t('dashboard.allHostels')}</p>
        </Link>
        <Link to="/owner/hostels" search={{ status: 'ACTIVE' }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-green-300 hover:shadow-md transition text-left block">
          <p className="text-2xl font-bold text-green-600">{activeHostels.length}</p>
          <p className="text-sm text-gray-500">{t('common.active')}</p>
        </Link>
        <Link to="/owner/hostels" search={{ status: 'INACTIVE' }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-red-300 hover:shadow-md transition text-left block">
          <p className="text-2xl font-bold text-red-500">{inactive}</p>
          <p className="text-sm text-gray-500">{t('common.inactive')}</p>
        </Link>
        <Link to="/owner/hostels" search={{ status: 'PENDING_APPROVAL' }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-amber-300 hover:shadow-md transition text-left block">
          <p className="text-2xl font-bold text-amber-500">{pending}</p>
          <p className="text-sm text-gray-500">{t('dashboard.pendingActivation')}</p>
        </Link>
      </div>
      )}

      {hostels.length === 0 && !isLoading && (
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <Building2 size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 mb-4">{t('hostel.noHostels')}</p>
          <Link to="/owner/hostels" search={{ status: '' }} className="text-teal-600 font-medium hover:underline">{t('hostel.addFirst')}</Link>
        </div>
      )}
    </div>
  );
}
